import axios, { AxiosInstance } from 'axios'
import { setAuthToken } from '../store/slices/authSlice'
import { addNotification, removeAllNotifications } from '../store/slices/notificationSlice'
import { v4 } from 'uuid'
import {
  CargoItemInterface,
  CargoListResponse,
  CargoPayload,
  CheckCodePayload,
  CheckCodeResponseAndToken,
  CityInterface,
  ClosePaymentPayload,
  DownloadInterface,
  GetCodePayload,
  GetCodeResponse,
  GetContactParams,
  ListParams,
  LoginResponse,
  ChatGroupActionPayload,
  PaymentPayload,
  PaymentResult,
  SlotItemInterface,
  SlotListResponse,
  SlotPayload,
  Topic,
  TransportItemInterface,
  TransportListResponse,
  TransportPayload,
  UpdateStatusPayload,
  SetExecutorPayload,
  BotAuthResponseAndToken,
  IdPayload,
  DeleteParams,
  BlockUserPayload,
  BlockedItemInterface,
  RouteInterface,
  ClusterInterface,
  ClusterListResponse,
  ClusterPayload,
  AuthResponse,
  RegisterEmailPayload,
  AccessPayload,
  UserInterface,
  LeadFormData,
  ExecutorClusterResponse,
} from '../common/types/interfaces'
import { CommonModals, Group, NotificationTypes } from '../common/types/enums'
import { store } from '../store/store'
import { resetCurrentUser, setCurrentUser } from '../store/slices/currentUserSlice'
import { getSalesApi } from '../salesApi/salesApi'
import { jwtDecode } from 'jwt-decode'
import { setCommonModalState } from '../store/slices/commonModalSlice'
import { clearClusterList } from '../store/slices/clusterListSlice'
import { resetCurrentCluster } from '../store/slices/currentClusterSlice'
import { setExecutorClusters } from '../store/slices/executorSlice'

// const host = window.location.hostname
// const baseUrl =
//   host === 'localhost'
//     ? 'http://https://logid.ru//api/v1/data'
//     : `https://${window.location.host}/api/v1/data`

// const authUrl =
//   host === 'localhost'
//     ? 'http://localhost:5002/api/v1/auth'
//     : `https://${window.location.host}/api/v1/auth`

// const host = ''
const host = 'https://logid.ru'
const baseUrl = `${host}/api/v1/data`
const authUrl = `${host}/api/v1/auth`

let api: Api | null = null

class Api {
  private readonly axiosInstance: AxiosInstance
  private store = store
  private slotListProgress = false
  private transportListProgress = false
  private cargoListProgress = false
  private timezone: string

  constructor(baseURL: string) {
    // 1. Определяем часовой пояс системы
    this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

    // 2. Создаём axios-инстанс
    this.axiosInstance = axios.create({ baseURL, timeout: 30000, withCredentials: true })

    // 3. Интерсептор ответов (обработка 401) — без изменений
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config
        if (originalRequest && error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true
          const result = await this.refresh()
          if (result) {
            const token = result.accessToken
            this.setToken(token)
          }
          return this.axiosInstance(originalRequest)
        }
        return Promise.reject(error)
      },
    )

    // 4. Интерсептор запросов — добавляем только Timezone
    this.axiosInstance.interceptors.request.use(
      (config) => {
        config.headers['X-Timezone'] = this.timezone // 👈 ТОЛЬКО ЭТО
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )
  }

  setToken(token: string) {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        config.headers.Authorization = store.getState().auth.token
          ? 'Bearer ' + store.getState().auth.token
          : 'Bearer ' + token
        config.headers['X-Timezone'] = this.timezone // 👈 ДОБАВИТЬ
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )
  }

  // Добавляем приватный метод очистки
  private clearSession(): void {
    // 1. Очищаем localStorage
    localStorage.removeItem('refresh')
    localStorage.removeItem('currentUser')
    localStorage.removeItem('currentCluster')

    // 2. Очищаем Redux состояние
    this.store.dispatch(setAuthToken(''))
    this.store.dispatch(resetCurrentUser())
    this.store.dispatch(resetCurrentCluster())
    this.store.dispatch(clearClusterList())
    this.store.dispatch(removeAllNotifications())

    // 3. Удаляем токен из заголовков axios
    delete this.axiosInstance.defaults.headers.common['Authorization']
  }

  // Обновлённый logout()
  logout(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const refreshToken = localStorage.getItem('refresh')

        await axios.get<string>(`${authUrl}/logout`, {
          headers: { Authorization: 'Bearer ' + refreshToken },
          withCredentials: true,
          timeout: 30000,
        })

        this.clearSession()

        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Вы не авторизованы. При необходимости, авторизуйтесь повторно.',
          }),
        )

        resolve()
      } catch (error: any) {
        this.clearSession()
        reject(error)
      }
    })
  }

  // Обновлённый forceLogout()
  forceLogout(): void {
    this.clearSession()

    this.store.dispatch(
      addNotification({
        id: v4(),
        type: NotificationTypes.ERROR,
        text: 'Пожалуйста, авторизуйтесь.',
      }),
    )

    // Открываем модалку логина
    this.store.dispatch(
      setCommonModalState({
        modal: CommonModals.LOGIN,
        state: true,
      }),
    )
  }

  refresh(): Promise<LoginResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const refreshToken = localStorage.getItem('refresh')
        if (!refreshToken) {
          reject(new Error('No refresh token'))
          return
        }

        const result = await axios.get<LoginResponse>(`${authUrl}/refresh`, {
          headers: {
            Authorization: 'Bearer ' + refreshToken,
          },
          withCredentials: true,
          timeout: 30000,
        })

        await this.handleAuthResponse(result.data)

        // Обновляем токен в salesApi
        const salesApi = getSalesApi()
        salesApi.setToken(result.data.accessToken)

        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  getCode(payload: GetCodePayload): Promise<GetCodeResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await axios.post<GetCodeResponse>(`${authUrl}/telegram/get_code`, payload, {
          withCredentials: true,
          timeout: 10000,
        })
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  checkCode(payload: CheckCodePayload): Promise<CheckCodeResponseAndToken> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await axios.post<CheckCodeResponseAndToken>(
          `${authUrl}/telegram/check_code`,
          payload,
          {
            withCredentials: true,
            timeout: 30000,
          },
        )

        await this.handleAuthResponse(result.data)

        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: 'Вы авторизованы',
          }),
        )

        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  botLogin(payload: any): Promise<AuthResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await axios.post<AuthResponse>(`${authUrl}/telegram/bot_login`, payload, {
          withCredentials: true,
          timeout: 30000,
        })

        await this.handleAuthResponse(result.data)
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  botRegister(payload: any): Promise<AuthResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await axios.post<AuthResponse>(`${authUrl}/telegram/bot_register`, payload, {
          withCredentials: true,
          timeout: 30000,
        })

        await this.handleAuthResponse(result.data)
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  private getUserFromToken(token: string): UserInterface | null {
    try {
      const decoded = jwtDecode<AccessPayload>(token)
      return {
        id: decoded.userId,
        phone_number: decoded.phone_number || '',
        uuid: decoded.uuid || '',
        contacts: decoded.contacts || 0,
        email: decoded.email || '',
        user_clusters: decoded.user_clusters || [],
      }
    } catch {
      return null
    }
  }

  register(payload: RegisterEmailPayload): Promise<AuthResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await axios.post<AuthResponse>(`${authUrl}/register`, payload, {
          withCredentials: true,
          timeout: 30000,
        })

        await this.handleAuthResponse(result.data)
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }
  // api.ts - методы для email (без Promise)

  async requestEmailCode(payload: { email: string }): Promise<{ message: string }> {
    try {
      const result = await axios.post(`${authUrl}/request-code`, payload, {
        withCredentials: true,
        timeout: 30000,
      })
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  async requestAttachEmailCode(payload: { email: string }): Promise<{ message: string }> {
    try {
      const token = store.getState().auth.token // accessToken
      const result = await axios.post(`${authUrl}/request-attach-code`, payload, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        withCredentials: true,
        timeout: 30000,
      })
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  async verifyEmailCode(payload: { email: string; code: string }): Promise<AuthResponse> {
    try {
      const result = await axios.post<AuthResponse>(`${authUrl}/verify-code`, payload, {
        withCredentials: true,
        timeout: 30000,
      })

      await this.handleAuthResponse(result.data)

      return result.data
    } catch (error: any) {
      throw error
    }
  }

  private async handleAuthResponse(response: AuthResponse): Promise<void> {
    if (!response.accessToken || !response.refreshToken) return

    const userFromToken = this.getUserFromToken(response.accessToken)

    if (userFromToken) {
      localStorage.setItem('currentUser', JSON.stringify(userFromToken))
      store.dispatch(setCurrentUser(userFromToken))
    }

    localStorage.setItem('refresh', response.refreshToken)
    store.dispatch(setAuthToken(response.accessToken))
    this.setToken(response.accessToken)

    const salesApi = getSalesApi()
    salesApi.setToken(response.accessToken)

    // ❌ Загружаем кластеры исполнителя
    try {
      const result = await this.axiosInstance.get('/executor/clusters')
      store.dispatch(setExecutorClusters(result.data))
    } catch (error) {
      store.dispatch(setExecutorClusters([]))
    }
  }

  download(payload: DownloadInterface): Promise<Blob> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await axios.get(`${this.axiosInstance.defaults.baseURL}/file`, {
          params: payload,
          responseType: 'blob',
          withCredentials: true,
        })
        resolve(result.data)
      } catch (error) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: `Не удалось получить файл ${payload}`,
          }),
        )
        reject(error)
      }
    })
  }

  createSlot(payload: SlotPayload): Promise<SlotItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/slot', payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы создали cлот`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось создать слот',
          }),
        )
        reject(error)
      }
    })
  }

  updateSlot(payload: SlotPayload): Promise<SlotItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, ...rest } = payload
        const result = await this.axiosInstance.put(`/slot/${id}`, rest, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы отредактировали слот`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось отредактировать слот',
          }),
        )
        reject(error)
      }
    })
  }

  createTransport(payload: TransportPayload): Promise<TransportItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/transport', payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы создали транспорт`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось создать транспорт',
          }),
        )
        reject(error)
      }
    })
  }

  updateTransport(payload: TransportPayload): Promise<TransportItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, ...rest } = payload
        const result = await this.axiosInstance.put(`/transport/${id}`, rest, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы отредактировали транспорт`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось отредактировать транспорт',
          }),
        )
        reject(error)
      }
    })
  }

  createCargo(payload: CargoPayload): Promise<CargoItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/cargo', payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы создали груз`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось создать груз',
          }),
        )
        reject(error)
      }
    })
  }

  updateCargo(payload: CargoPayload): Promise<CargoItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, ...rest } = payload
        const result = await this.axiosInstance.put(`/cargo/${id}`, rest, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы отредактировали груз`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось отредактировать груз',
          }),
        )
        reject(error)
      }
    })
  }

  updateStatus(payload: UpdateStatusPayload): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, route, ...rest } = payload
        const result = await this.axiosInstance.put(`${route}/status/${id}`, rest)
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось обновить статус',
          }),
        )
        reject(error)
      }
    })
  }

  getSlotList(params?: ListParams): Promise<SlotListResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.slotListProgress) {
          this.slotListProgress = true
          const result = await this.axiosInstance.get<SlotListResponse>('/slot/auth', { params })
          this.slotListProgress = false
          resolve(result.data)
        }
        reject()
      } catch (error: any) {
        this.slotListProgress = false
        reject(error)
      }
    })
  }

  getAdSlotList(params?: ListParams): Promise<SlotListResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.slotListProgress) {
          const result = await this.axiosInstance.get<SlotListResponse>('/slot', { params })
          this.slotListProgress = false
          resolve(result.data)
        }
        reject()
      } catch (error: any) {
        this.slotListProgress = false
        reject(error)
      }
    })
  }

  getSlot(id: number): Promise<SlotItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.get<SlotItemInterface>(`/slot/auth/${id}`)
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }
  getAdSlot(id: number): Promise<SlotItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.get<SlotItemInterface>(`/slot/${id}`)
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  getTransportList(params?: ListParams): Promise<TransportListResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.transportListProgress) {
          this.transportListProgress = true
          const result = await this.axiosInstance.get<TransportListResponse>('/transport/auth', {
            params,
          })
          this.transportListProgress = false
          resolve(result.data)
        }
      } catch (error: any) {
        this.transportListProgress = false
        reject(error)
      }
    })
  }
  getAdTransportList(params?: ListParams): Promise<TransportListResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.transportListProgress) {
          this.transportListProgress = true
          const result = await this.axiosInstance.get<TransportListResponse>('/transport', {
            params,
          })
          this.transportListProgress = false
          resolve(result.data)
        }
        reject()
      } catch (error: any) {
        this.transportListProgress = false
        reject(error)
      }
    })
  }

  getTransport(id: number): Promise<TransportItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.get<TransportItemInterface>(`/transport/auth/${id}`)
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }
  getAdTransport(id: number): Promise<TransportItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.get<TransportItemInterface>(`/transport/${id}`)
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  getCargoList(params?: ListParams): Promise<CargoListResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.cargoListProgress) {
          this.cargoListProgress = true
          const result = await this.axiosInstance.get<TransportListResponse>('/cargo/auth', {
            params,
          })
          this.cargoListProgress = false
          resolve(result.data)
        }
        reject()
      } catch (error: any) {
        this.cargoListProgress = false
        reject(error)
      }
    })
  }

  getAdCargoList(params?: ListParams): Promise<CargoListResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.cargoListProgress) {
          this.cargoListProgress = true
          const result = await this.axiosInstance.get<TransportListResponse>('/cargo', { params })
          this.cargoListProgress = false
          resolve(result.data)
        }
        reject()
      } catch (error: any) {
        reject(error)
      }
    })
  }

  getCargo(id: number): Promise<CargoItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.get<CargoItemInterface>(`/cargo/auth/${id}`)
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }
  getAdCargo(id: number): Promise<CargoItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.get<CargoItemInterface>(`/cargo/${id}`)
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  getCityList(): Promise<CityInterface[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.get<CityInterface[]>('/city')
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  getContact(params: GetContactParams): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.get<string>('/user/contact', { params })
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось получить контакт',
          }),
        )
        reject(error)
      }
    })
  }

  createPayment(payload: PaymentPayload): Promise<PaymentResult> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post<PaymentResult>('/payment', payload)
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось начать платеж',
          }),
        )
        reject(error)
      }
    })
  }

  closePayment(payload: ClosePaymentPayload): Promise<PaymentResult> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, ...rest } = payload
        const result = await this.axiosInstance.put<PaymentResult>(`/payment/${id}`, rest)
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Ошибка завершения платежа',
          }),
        )
        reject(error)
      }
    })
  }

  //Chat...

  markAsRead(payload: ChatGroupActionPayload): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.put<string>(`chat/mark_as_read`, payload)
        resolve(result.data)
      } catch (error: any) {
        // this.store.dispatch(
        //   addNotification({
        //     id: v4(),
        //     type: NotificationTypes.ERROR,
        //     text: 'Ошибка пометки сообщений как прочитанных',
        //   }),
        // )
        reject(error)
      }
    })
  }

  archiveTopic(payload: ChatGroupActionPayload): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.put<string>(`chat/archive`, payload)
        resolve(result.data)
      } catch (error: any) {
        // this.store.dispatch(
        //   addNotification({
        //     id: v4(),
        //     type: NotificationTypes.ERROR,
        //     text: 'Ошибка архивирования сообщений',
        //   }),
        // )
        reject(error)
      }
    })
  }

  unArchiveTopic(payload: ChatGroupActionPayload): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.put<string>(`chat/unarchive`, payload)
        resolve(result.data)
      } catch (error: any) {
        // this.store.dispatch(
        //   addNotification({
        //     id: v4(),
        //     type: NotificationTypes.ERROR,
        //     text: 'Ошибка разархивирования сообщений',
        //   }),
        // )
        reject(error)
      }
    })
  }

  deleteTopic(payload: ChatGroupActionPayload): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.put<string>(`chat/delete`, payload)
        resolve(result.data)
      } catch (error: any) {
        // this.store.dispatch(
        //   addNotification({
        //     id: v4(),
        //     type: NotificationTypes.ERROR,
        //     text: 'Ошибка удаления сообщений',
        //   }),
        // )
        reject(error)
      }
    })
  }

  createTopic(payload: Topic): Promise<Topic> {
    return new Promise(async (resolve, reject) => {
      try {
        const currentUser = this.store.getState().currentUser.user
        if (payload.userId === payload.participantId) {
          this.store.dispatch(
            addNotification({
              id: v4(),
              type: NotificationTypes.ERROR,
              text: 'Не удалось начать чат',
            }),
          )
          return reject()
        }
        const result = await this.axiosInstance.post<Topic>(
          `/chat/${currentUser ? 'topic' : 'ad_topic'}`,
          payload,
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось начать чат',
          }),
        )
        reject(error)
      }
    })
  }

  getTopicList(payload: number): Promise<Topic[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.get<Topic[]>(`/chat/topic_list/${payload}`)
        resolve(result.data)
      } catch (error: any) {
        // this.store.dispatch(
        //   addNotification({
        //     id: v4(),
        //     type: NotificationTypes.ERROR,
        //     text: 'Не удалось получить список тем',
        //   }),
        // )
        reject(error)
      }
    })
  }

  setExecutor(payload: SetExecutorPayload): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.put<string>(`/chat/set_executor`, payload)
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось назначить исполнителя',
          }),
        )
        reject(error)
      }
    })
  }

  addSelected(payload: IdPayload, group: Group): Promise<IdPayload> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post(`/${group}/add_selected`, payload)
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не добавить в избранное',
          }),
        )
        reject(error)
      }
    })
  }

  deleteSelected(params: DeleteParams, group: Group): Promise<number> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.delete<number>(
          `/${group}/delete_selected/${params.id}`,
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось удалить из избранного',
          }),
        )
        reject(error)
      }
    })
  }

  addView(payload: IdPayload, group: Group): Promise<IdPayload> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post(`/${group}/add_view`, payload)
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  addContact(payload: IdPayload, group: Group): Promise<IdPayload> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post(`/${group}/add_contact`, payload)
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  blockUser(payload: BlockUserPayload): Promise<BlockUserPayload> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post(`/user/block`, payload)
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: 'Вы заблокировали пользователя',
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось заблокировать пользователя',
          }),
        )
        reject(error)
      }
    })
  }

  unBlockUser(payload: BlockUserPayload): Promise<BlockUserPayload> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.delete(`/user/unblock/${payload.user_id}`)
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: 'Вы разблокировали пользователя',
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось разаблокировать пользователя',
          }),
        )
        reject(error)
      }
    })
  }
  getBlockedList(): Promise<BlockedItemInterface[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.get<BlockedItemInterface[]>('/user/blocked')
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  getRoute(id: number): Promise<RouteInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.get<RouteInterface>(`/map/route/${id}`)
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось получить маршрут',
          }),
        )
        reject(error)
      }
    })
  }

  async createCluster(payload: ClusterPayload): Promise<ClusterInterface> {
    try {
      const result = await this.axiosInstance.post('/cluster', payload)
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.SUCCESS,
          text: `Кластер создан`,
        }),
      )
      return result.data
    } catch (error: unknown) {
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: 'Не удалось создать кластер',
        }),
      )
      throw error
    }
  }

  async updateCluster(payload: ClusterPayload): Promise<ClusterInterface> {
    try {
      const { id, ...rest } = payload
      const result = await this.axiosInstance.put(`/cluster/${id}`, rest, {})

      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.SUCCESS,
          text: `Вы отредактировали кластер`,
        }),
      )

      return result.data
    } catch (error: unknown) {
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: 'Не удалось отредактировать кластер',
        }),
      )

      throw error
    }
  }

  async getClusterList(params?: ListParams): Promise<ClusterListResponse | undefined> {
    try {
      const result = await this.axiosInstance.get<ClusterListResponse>('/cluster', { params })
      if (result) return result.data
      return undefined
    } catch (error: any) {
      throw error
    }
  }

  async getPublicClusterList(params?: {
    target?: 'logid' | 'logplace' | 'logmall'
  }): Promise<ClusterInterface[]> {
    try {
      const result = await this.axiosInstance.get<ClusterInterface[]>('/cluster/public/list', {
        params,
      })
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  async sendLead(payload: LeadFormData): Promise<{ success: boolean }> {
    try {
      const result = await this.axiosInstance.post('/mail/send-lead', payload)
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.SUCCESS,
          text: `Ваша заявка отправлена. Мы свяжемся с вами в ближайшее время`,
        }),
      )
      return result.data
    } catch (error: any) {
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: 'Не удалось отправить заявку. Попробуйте позже',
        }),
      )
      throw error
    }
  }

  startPayment(data: {
    clusterId: number
    durationMonths: number
    price: number
    description: string
    quantity: number
  }): Promise<{ token: string; id: string }> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/payment', data)
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  sendInvoice(data: { clusterId: number; durationMonths: number }): Promise<{ message: string }> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/invoice/send', data)
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: 'Счёт отправлен на почту',
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось отправить счёт',
          }),
        )
        reject(error)
      }
    })
  }

  getCluster(clusterId: number): Promise<ClusterInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.get<ClusterInterface>(`/cluster/${clusterId}`)
        resolve(result.data)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  async linkExecutorByInvite(code: string) {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/executor/link', { code })

        const clusterName = result.data?.executor?.clusterName || 'кластеру'

        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы привязаны к ${clusterName}. Подробности в личном кабинете.`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        // Если ошибка авторизации — открываем модалку, тост НЕ показываем
        if (error?.response?.status === 401 || error?.message === 'No refresh token') {
          this.store.dispatch(
            setCommonModalState({
              modal: CommonModals.LOGIN,
              state: true,
            }),
          )
          reject(error)
          return
        }

        // Другие ошибки — показываем тост
        const errorMessage =
          error?.response?.data?.error || 'Не удалось привязать вас как исполнителя'
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: errorMessage,
          }),
        )
        reject(error)
      }
    })
  }

  async getExecutorClusters(): Promise<ExecutorClusterResponse[]> {
    try {
      const result = await this.axiosInstance.get('/executor/clusters')
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  extendBilling(data: {
    clusterId: number
    paidUntil: string
  }): Promise<{ clusterId: number; paidUntil: string }> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/billing/extend', data)
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: 'Дата оплаты обновлена',
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось продлить подписку',
          }),
        )
        reject(error)
      }
    })
  }
}

export const getApi = () => {
  if (!api) api = new Api(baseUrl)
  return api
}
